import { useState } from 'react';
import type { MemberPack } from '../api';
import Icon from '../design/Icon';
import { fmtDateShort, fmtTime } from '../design/atoms';

/* A pack the member bought — "3 of 5 used" — and, on tap, the bookings behind
   the 3. The figures are the server's (GET /member/packs): the same usage
   booking derives, so this never disagrees with what the Book tab will allow.
   The mobile app's counterpart is components/billing/PackUsageCard.tsx. */

const STATUS_WORD: Record<string, string> = { booked: 'Booked', attended: 'Attended', no_show: 'Missed' };

/** "2 left · expires 4 Dec" / "Used up" / "Expired 4 Dec · 2 unused". */
export function packSummary(p: MemberPack): string {
  const until = p.validTo ? fmtDateShort(p.validTo.slice(0, 10)) : null;
  if (p.state === 'expired') return `Expired${until ? ` ${until}` : ''}${p.remaining > 0 ? ` · ${p.remaining} unused` : ''}`;
  if (p.state === 'used_up') return 'All used';
  const left = `${p.remaining} left${p.period ? ` this ${p.period}` : ''}`;
  return until ? `${left} · valid till ${until}` : left;
}

export default function PackUsageCard({ pack, onInvoice }: { pack: MemberPack; onInvoice?: () => void }) {
  const [open, setOpen] = useState(false);
  const pct = pack.total > 0 ? Math.min(100, Math.round((pack.used / pack.total) * 100)) : 0;
  const over = pack.state !== 'active';

  return (
    <div className={`card ${over ? 'flat' : ''}`} style={{ padding: 14 }}>
      <button className="between" style={{ width: '100%', textAlign: 'left' }} aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <div className="row" style={{ gap: 11, minWidth: 0 }}>
          <div className={`li ${over ? '' : 'gold'}`}><Icon n="gift" s={17} c={over ? 'var(--ink-3)' : 'var(--gold-2)'} /></div>
          <div className="col" style={{ gap: 1, minWidth: 0 }}>
            <div className="mid trunc">{pack.name}</div>
            <div className="tiny trunc">{packSummary(pack)}</div>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <span className="num">{pack.used}/{pack.total}</span>
          <Icon n={open ? 'up' : 'right'} s={16} c="var(--ink-3)" />
        </div>
      </button>

      <div className="bar" style={{ marginTop: 12 }} role="img" aria-label={`${pack.used} of ${pack.total} used`}>
        <i style={{ width: `${pct}%` }} />
      </div>

      {open && (
        <div style={{ marginTop: 10 }}>
          <div className="lbl">Used for</div>
          {pack.usage.length === 0 && pack.carriedOver === 0 ? (
            <div className="tiny wrap" style={{ marginTop: 6 }}>Nothing yet — book a session and it shows up here.</div>
          ) : (
            <div>
              {pack.usage.map(u => (
                <div key={u.bookingId} className="lrow">
                  <div className="col" style={{ gap: 2 }}>
                    <div className="mid trunc">{u.name}</div>
                    <div className="tiny trunc">{fmtDateShort(u.date)} · {fmtTime(u.startTime)}</div>
                  </div>
                  {u.units > 1 && <span className="tag">{u.units} used</span>}
                  <span className={`tag ${u.status === 'attended' ? 'sage' : u.status === 'no_show' ? 'clay' : ''}`}>{STATUS_WORD[u.status] ?? u.status}</span>
                </div>
              ))}
              {pack.carriedOver > 0 && (
                <div className="lrow">
                  <div className="col"><div className="tiny wrap">Used before bookings moved to the app</div></div>
                  <span className="tag">{pack.carriedOver} used</span>
                </div>
              )}
            </div>
          )}
          {onInvoice && (
            <button className="btn ghost sm block" style={{ marginTop: 10 }} onClick={onInvoice}>
              View invoice{pack.invoiceId ? ` · ${pack.invoiceId}` : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
